import React, { useEffect, useState } from 'react';
import SearchableMultiSelect from './SearchableMultiSelect';
import api from '../../utils/api';

// Meta custom conversions as report metrics. Unlike the standard metrics (a fixed
// list mirrored from the server's METRIC_META), these are per ad account and are
// fetched live from /meta-ads/custom-conversions. Each selected one becomes its
// own column, resolved server-side from the insights `actions` array — see
// appendCustomConversionCells in server/services/cognitive-flows/metaAdsReportService.js.
//
// Shared by the two Meta editors that must stay in sync:
// components/nodes/AdsReadConfig.js and pages/CognitiveFlowsDataSourcesPage.jsx.

// Fixed-column reports emit their builder's columns and ignore metric selection.
// Mirrors META_FIXED_COLUMN_REPORTS in server metaAdsReportService.js.
const FIXED_COLUMN_REPORTS = new Set(['ad_enhancements', 'adset_advantage', 'creative', 'adset_targeting', 'change_history']);

/** True when custom conversions apply to this report (Meta insights reports only). */
export function supportsCustomConversions(platform, reportType) {
  return platform === 'meta' && !!reportType && !FIXED_COLUMN_REPORTS.has(reportType);
}

/** The account's custom conversions, refetched whenever the account changes. */
export function useMetaCustomConversions(accountId, { enabled = true } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !accountId) { setList([]); setError(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get('/meta-ads/custom-conversions', { params: { accountId } })
      .then(({ data }) => { if (!cancelled) { setList(data || []); setError(null); } })
      .catch((err) => {
        if (cancelled) return;
        setList([]);
        setError(err?.response?.data?.error || 'Could not load custom conversions');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, enabled]);

  return { customConversions: list, loading, error };
}

/**
 * Searchable multi-select of the account's custom conversions.
 * `selected` / `onChange` carry [{ id, name }] — the name is persisted alongside
 * the id so a saved source keeps a stable column header even if the conversion is
 * later renamed or deleted on Meta's side.
 *
 * `styles` is the host editor's style object; it must provide `hint` (and `input`,
 * used for the trigger; both mirrors already define them).
 */
export default function CustomConversionsSelector({
  accountId,
  selected = [],
  onChange,
  disabled = false,
  styles,
  includeCost = false,
  onIncludeCostChange,
  includeValue = false,
  onIncludeValueChange,
}) {
  const { customConversions, loading, error } = useMetaCustomConversions(accountId);

  // The toggles live inside this component rather than in each host, so every
  // Meta editor picks them up without growing its own copy. Cost and value are
  // siblings: each adds one companion column per selection.
  const toggle = (checked, onToggle, text) => (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      fontSize: '12px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
    }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled}
        style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
      />
      <span>{text}</span>
    </label>
  );
  const costToggle = onIncludeCostChange ? toggle(includeCost, onIncludeCostChange, 'Include cost per conversion') : null;
  const valueToggle = onIncludeValueChange ? toggle(includeValue, onIncludeValueChange, 'Include conversion value') : null;
  const toggleRow = (costToggle || valueToggle) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginTop: '6px' }}>
      {costToggle}
      {valueToggle}
    </div>
  ) : null;

  const withToggles = (body) => <>{body}{toggleRow}</>;

  if (!accountId) return <span style={styles.hint}>Select an ad account to load its custom conversions.</span>;
  if (loading) return <span style={styles.hint}>Loading custom conversions…</span>;
  if (error) return <span style={styles.hint}>{error}</span>;

  // Nothing to show, but a saved source may still carry selections from another
  // account — surface those so they can be cleared rather than silently stranded.
  if (!customConversions.length && !selected.length) {
    return <span style={styles.hint}>This ad account has no custom conversions.</span>;
  }

  const known = new Set(customConversions.map((c) => String(c.id)));
  const stranded = selected.filter((c) => !known.has(String(c.id)));

  const options = [
    ...customConversions.map((cc) => ({
      value: String(cc.id),
      label: cc.name,
      hint: cc.customEventType || undefined,
    })),
    ...stranded.map((cc) => ({
      value: String(cc.id),
      label: `${cc.name} (not in this account)`,
      hint: 'Selected on a different ad account — untick to remove the column.',
    })),
  ];

  // Selections round-trip as [{id,name}]; the picker itself only deals in ids, so the
  // name is recovered from the fetched list (or kept from the existing selection for a
  // stranded one, which is the whole reason the name is persisted).
  const nameFor = (id) =>
    customConversions.find((c) => String(c.id) === id)?.name
    ?? selected.find((c) => String(c.id) === id)?.name
    ?? id;

  return withToggles(
    <SearchableMultiSelect
      value={selected.map((c) => String(c.id))}
      options={options}
      onChange={(ids) => onChange(ids.map((id) => ({ id, name: nameFor(id) })))}
      styles={styles}
      disabled={disabled}
      placeholder="— Select custom conversions —"
      emptyText="No matching conversions"
      summarize={(n) => `${n} conversions selected`}
    />
  );
}
